import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';

/**
 * A destructive action behind the thing's own name, typed out.
 *
 * There are two of these — deleting a tool and unpublishing a tenant — and both
 * are irreversible from this screen: the manifest on disk changes and every agent
 * holding the endpoint loses the tool. A bare "Delete" that fires on one click is
 * the wrong control for that, and a browser `confirm()` dialog is the same click
 * with a different shape. Typing the name is the only cheap gate that requires the
 * operator to have READ which row they are on, which is the mistake actually worth
 * preventing on a page listing many tools that differ by one word.
 */
export function TypedConfirm(props: {
  /** Distinguishes this confirmation's field from every other on the page. */
  id: string;
  phrase: string;
  typed: string;
  prompt: VNode | string;
  actionLabel: string;
  busy?: boolean;
  onType: (value: string) => void;
  onConfirm: () => void;
}): VNode {
  const armed = props.typed === props.phrase;
  const fieldId = `confirm-${props.id}`;

  return html`
    <form
      class="confirm"
      onSubmit=${(e: Event) => {
        e.preventDefault();
        if (armed && !props.busy) props.onConfirm();
      }}
    >
      <p class="confirm__prompt">${props.prompt}</p>
      <label class="confirm__label" for=${fieldId}>
        Type${SP}<code>${props.phrase}</code>${SP}to enable the button
      </label>
      <div class="confirm__row">
        <input
          id=${fieldId}
          class="confirm__input"
          type="text"
          autocomplete="off"
          spellcheck="false"
          autocapitalize="off"
          value=${props.typed}
          onInput=${(e: Event) => props.onType((e.target as HTMLInputElement).value)}
        />
        <button type="submit" class="btn btn--danger" disabled=${!armed || props.busy === true}>
          ${props.busy ? 'Working…' : props.actionLabel}
        </button>
      </div>
    </form>
  `;
}
